import { apiRequest } from '../api';
import { state } from '../state';
import { navigateTo } from '../router';
import { t } from '../i18n';
import { attachWhoTooltip, whoNames } from './admin-who';

// ============================================================
// Кнопка «Подписаться» на игрока (уведомления о победах и стримах).
// Это обычная кнопка в общем ряду действий — статус подгружается
// отдельно, чтобы не задерживать карточку.
// ============================================================

interface FollowStatus {
  available: boolean;
  following: boolean;
  followers: number;
}

export function buildFollowButton(playerId: number): HTMLButtonElement {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'btn-secondary follow-btn';
  button.disabled = true;
  button.title = t('follow.hint');

  const icon = document.createElement('span');
  icon.className = 'follow-icon';
  icon.setAttribute('aria-hidden', 'true');
  icon.textContent = '☆';

  const label = document.createElement('span');
  label.className = 'follow-label';
  label.textContent = t('follow.button');

  const count = document.createElement('span');
  count.className = 'follow-count hidden';

  button.append(icon, label, count);

  attachWhoTooltip(button, 'player', playerId, (who) => {
    if (who.followers.length === 0) return null;
    return `${t('who.followers')}: ${whoNames(who.followers)}`;
  });

  let status: FollowStatus | null = null;

  const render = (): void => {
    if (!status) return;

    if (!status.available) {
      button.disabled = true;
      button.classList.add('is-unavailable');
      button.title = t('follow.unavailable');
      label.textContent = t('follow.unavailable');
      icon.textContent = '☆';
      count.classList.add('hidden');
      return;
    }

    button.disabled = false;
    button.classList.remove('is-unavailable');
    button.classList.toggle('is-active', status.following);
    button.title = state.token ? t('follow.hint') : t('follow.login');

    icon.textContent = status.following ? '★' : '☆';
    label.textContent = status.following ? t('follow.following') : t('follow.button');

    count.textContent = status.followers > 0 ? String(status.followers) : '';
    count.title = t('follow.count', { n: status.followers });
    count.classList.toggle('hidden', status.followers <= 0);
  };

  const load = async (): Promise<void> => {
    try {
      status = await apiRequest<FollowStatus>(`/api/follows/${playerId}`, {
        token: state.token,
      });
    } catch {
      status = { available: false, following: false, followers: 0 };
    }
    render();
  };

  button.addEventListener('click', () => {
    if (!status?.available) return;

    if (!state.token) {
      sessionStorage.setItem(
        'redirectAfterLogin',
        window.location.pathname + window.location.search,
      );
      navigateTo('/auth');
      return;
    }

    void (async () => {
      const next = !status!.following;
      button.disabled = true;
      try {
        status = await apiRequest<FollowStatus>(`/api/follows/${playerId}`, {
          method: next ? 'PUT' : 'DELETE',
          token: state.token,
        });
      } catch (err) {
        console.warn('[follow] toggle failed:', err);
        status = { ...status!, following: !next };
        button.title = t('follow.error');
      }
      render();
    })();
  });

  void load();
  return button;
}
